/*
  Drives the add-to-cart that sits at the end of the product description.

  Like the sticky bar, it never adds to the cart itself -- its button clicks
  the real one inside <product-form>. The selected variant, the quantity,
  sold-out handling, Dawn's AJAX cart and its error messages all keep working
  from one place instead of living twice and drifting apart.

  The real button is re-read on every sync rather than held onto. Dawn swaps
  the whole form out when the variant changes, so a reference captured at
  connect time can end up pointing at a node that is no longer on the page --
  the button would then mirror a dead element and forward clicks into nothing.
*/
(function () {
  'use strict';

  const FORM = 'product-form';
  const REAL_BUTTON = 'product-form button[name="add"]';

  class DescriptionAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-description-atc-add]');
      this.label = this.querySelector('[data-description-atc-label]');
      this.defaultLabel = this.label ? this.label.textContent.trim() : '';

      // No buy button on the page at all -- a product with nothing for sale.
      // Nothing to forward to, so there is nothing worth showing.
      if (!this.realButton()) {
        this.hidden = true;
        return;
      }

      this.button.addEventListener('click', this.forwardClick.bind(this));
      this.mirrorButton();
    }

    disconnectedCallback() {
      this.formWatcher?.disconnect();
    }

    realButton() {
      return document.querySelector(REAL_BUTTON);
    }

    forwardClick(event) {
      event.preventDefault();
      const real = this.realButton();
      if (!real || real.disabled) return;
      real.click();
    }

    /*
      Dawn rewrites the real button's text and toggles `disabled` as the
      variant changes -- "Sold out", "Unavailable", back again. Copying both
      keeps this from offering something the page has already refused.

      The custom label is kept while the product can actually be bought, and
      stood aside for Dawn's wording the moment it cannot, so a sold-out
      variant never sits behind a button that still says Add to cart.

      Watches the form rather than the button, because Dawn replaces the
      button node outright on a variant change and an observer bound to the
      old node would simply stop reporting.
    */
    mirrorButton() {
      const sync = () => {
        const real = this.realButton();
        if (!real) {
          this.hidden = true;
          return;
        }
        this.hidden = false;
        this.button.disabled = real.disabled;
        if (!this.label) return;
        const realText = real.textContent.trim();
        this.label.textContent = real.disabled && realText ? realText : this.defaultLabel;
      };

      sync();
      const form = document.querySelector(FORM);
      if (!form) return;
      this.formWatcher = new MutationObserver(sync);
      this.formWatcher.observe(form, {
        attributes: true,
        attributeFilter: ['disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  }

  if (!customElements.get('description-atc')) {
    customElements.define('description-atc', DescriptionAtc);
  }
})();
